// First, so an error while the page starts is told on its failure screen (M288).
import { hasFailed, markStarted, showFailure } from './ui/failure';
import { plainTitle } from './ui/surveyNames';
import { createScene, type Scene } from './render/scene';
import { visibleRect } from './render/fit';
import { AquariumView } from './render/aquariumView';
import { Hud } from './ui/hud';
import { PAUSED, type SpeedSetting } from './ui/speed';
import { bindKeyboard } from './platform/input';
import { AUTOSAVE_SLOT, QUICK_SLOT, readSlot, writeSlot, NO_SAVE, UNREADABLE_SLOT, keepSlot } from './platform/saves';
import { createWorld } from './sim/world';
import { civilFromEpochDay } from './sim/calendar';
import { tick } from './sim/sim';
import { applyCommand } from './sim/commands';
import type { GameEvent, World } from './sim/types';
import { reefOf, seafloorOf } from './sim/data/sites';
import { columnBleached, columnCovers } from './sim/coral';
import { dateLabel, epochDayOf } from './sim/calendar';
import { FISH_LIVES, fishCounts } from './sim/fish';
import { sanctuaryChlorophyll, sanctuaryPlankton } from './sim/systems/plankton';
import { FISH_KINDS } from './render/fishKinds';
import { civilDuskHour, daySun, moonAtHour, sunAtHour, sunLongitudeDeg } from './render/sunlight';
import { ObservationClock, PRESET_NAMES, clockLabel, moonLit, observationLine, type CameraView } from './ui/observation';
import { SYNODIC_MONTH_DAYS, moonAgeDays, noonUtc, solarDeclinationDeg } from './sim/astronomy';
import { EXHIBIT_MIN_DEPTH_M, exhibitCover, exhibitOf, type Exhibit, type Habitat } from './render/exhibit';
import { ExhibitView, exhibitChoices, exhibitLook } from './render/exhibitView';
import { SPECIES_NAMES, dolphinCard, isCountedFish, honuCard, octopusCard, oneFishCard, sealCard, slopeFishCard, speciesVisitorCard, visitorCard, whaleCard, wildlifeCard, type Seen, type WildlifeCard } from './ui/wildlife';
import { HONU, turtleCounts } from './sim/turtles';
import { HONU_DRAW, honuSprites } from './render/turtles';
import { SEAL_BODY, SEAL_DRAW, sealSprites, sealStages } from './render/seals';
import { HABITAT_NAMES, VISITOR_DRAW, daysIn, visitorId, whitetipActive, type Visitor } from './render/habitats/visitors';
import { YOUNG_SEEN_MM, speciesVisits } from './render/habitats/data';
import { rememberedFor, tagTargets, type MarkTarget, type TagMarks } from './render/tagMarks';
import { TAG_HABITATS, type TagHabitat } from './sim/tags';
import { goToOf, movedTo } from './ui/tagged';
import type * as THREE from 'three';
import { SPECIES_VISITS, type VisitorSpecies } from './sim/visits';
import { MONK_SEAL, sealCounts } from './sim/seals';
import { HUMPBACK, whaleCounts } from './sim/whales';
import { WHALE_DRAW, humpbackSprites } from './render/whales';
import type { FishSpeciesId } from './sim/fish';
import { ISLAND_NAMES, REEF_ISLANDS, type ReefIsland } from './sim/reef';
import { summarize } from './sim/seafloor';
import type { AquariumStyle } from './ui/hud';
import { shownPlace, type Place } from './ui/visit';
import type { SiteId } from './sim/data/sites';
import { columnKilled, reefModelOf, reefTextures, type ReefModel, type ReefTextures } from './render/reefOutcrops';
import { REEF_MODEL_URL } from './render/data/reefModel';
import { HabitatHost } from './render/habitats/host';
import { depthsOf, openHabitat, relayDue, type OpenedHabitat } from './render/habitats/scenes';
import { REEF_SCENE, ReefScene } from './render/habitats/reefScene';
import { mantleM } from './render/habitats/octopuses';
import { PELAGIC_SCENE, PelagicScene } from './render/habitats/pelagicScene';
import { BenthicScene } from './render/habitats/benthicScene';
import type { HourSun } from './render/sunlight';
import { Veil } from './ui/veil';
import { FRAME_METER_KEY, frameMeterShown, mountFrameMeter } from './ui/frameMeter';
import { mountHuntOverlay } from './ui/huntOverlay';
import { mountLifeOverlay } from './ui/lifeOverlay';
import { nextEvent, stopsAdvance } from './sim/nextEvent';
import { isResult, seasonKey, seasonNews } from './ui/results';
import { stopWords } from './ui/stopWords';
import { NO_3D_NOTICE, OFF_3D_NOTICE, STOPPED_3D_NOTICE, renderModeOf } from './ui/renderMode';
import { clockSteps, MAX_FRAME_MS, MAX_TICKS_PER_FRAME } from './ui/clock';
import { SUBSTITUTE_LIMITS, ViewSubstitute, type SubstituteChoice } from './ui/viewSubstitute';

/** The next event as its button tells it: what, and in how many days (M240). */
/** The next event read once a day and again on each command (M240's second review: read each frame, about 0.11 ms). */
let nextEventMemo: { day: number; commands: number; world: World; label: { label: string; busy: boolean } | null } | null = null;
function nextEventLabel(w: World): { label: string; busy: boolean } | null {
  if (nextEventMemo && nextEventMemo.world === w && nextEventMemo.day === w.day && nextEventMemo.commands === commandsApplied) return nextEventMemo.label;
  nextEventMemo = { day: w.day, commands: commandsApplied, world: w, label: nextEventLabelOf(w) };
  return nextEventMemo.label;
}
function nextEventLabelOf(w: World): { label: string; busy: boolean } | null {
  const e = nextEvent(w);
  if (!e) return null;
  const days = e.day - epochDayOf(w);
  // How far first, so a phone's narrow button still tells it (review 29: cut to the event's first word there).
  return { label: `${days} ${days === 1 ? 'day' : 'days'} · ${plainTitle(e.what)}`, busy: false };
}

/**
 * The browser entry: wires the core, the view, the HUD and the platform
 * together and runs the clock. The only file that knows all four exist.
 */

const canvas = document.getElementById('canvas') as HTMLCanvasElement;
const hudRoot = document.getElementById('hud') as HTMLElement;

/** A fresh world on a new seed, the page's first and each New game's (M63). The seed is the only thing the wall clock touches. */
const freshWorld = (): World => createWorld({ seed: Date.now() >>> 0 });
/**
 * The game the player left, opened where it was left (M237, the brief's "reliable autosaving"): the autosave's world if
 * it loads, else a new one. One the game cannot read (a newer version's, a damaged one) is kept aside first, in its own
 * slot, and the player told why (review 29: it was overwritten by the new game within thirty seconds, unsaid).
 */
const resumed = readSlot(AUTOSAVE_SLOT);
const unreadable = typeof resumed === 'string' && resumed !== NO_SAVE && keepSlot(AUTOSAVE_SLOT, UNREADABLE_SLOT) ? resumed : null;
let world: World = typeof resumed === 'string' ? freshWorld() : resumed;
/** The developer's tools (M237: the frame rate meter among them), on with `?dev` in the address, or kept on from before. */
const DEV_KEY = 'seven-seas.dev';
const devMode = ((): boolean => {
  try {
    const asked = new URLSearchParams(location.search).get('dev');
    if (asked !== null) localStorage.setItem(DEV_KEY, asked === '0' ? 'off' : 'on');
    return localStorage.getItem(DEV_KEY) === 'on';
  } catch {
    return new URLSearchParams(location.search).has('dev');
  }
})();

/** How the player asked to see the aquarium: the exhibit first (M94, the brief's; the diorama's place, M86), the real map a press away. */
let chosen: AquariumStyle = 'exhibit';
/** Where the map's camera looks from: the south-east and above (JUDGED, by eye: its islands seen whole at the opening view). */
const MAP_LOOK_FROM: readonly [number, number, number] = [12, 21, 16];
/**
 * The habitat shown, the reef first (the brief: "the default view on entering Maui Nui should be the shallow coral
 * reef"), in its own scene since M110 (the user's third brief: "Selecting Reef, Blue Water or another habitat should
 * activate a dedicated 3D scene composition"); or the whole tank, its panorama or all of it (Fit all); a visited
 * site's first habitat.
 */
let habitat: Habitat['id'] | 'overview' | 'all' = 'reef';
/** The site the world map visits (M98), or null for the player's own sanctuary. A way of looking, not the world: not saved. */
let visiting: SiteId | null = null;
/** The place shown, the day's: memoized, as a visited site's environment is reckoned from its region's state. */
let shownMemo: { world: World; day: number; visiting: SiteId | null; place: Place | undefined } | null = null;
const shown = (): Place | undefined => {
  if (shownMemo?.world !== world || shownMemo.day !== world.day || shownMemo.visiting !== visiting) {
    shownMemo = { world, day: world.day, visiting, place: shownPlace(world, visiting) };
  }
  return shownMemo.place;
};

/** An aquarium as drawn: its view, its style, its exhibit when it is one (its reef is what the cover is laid on), and whether its site has one at all. */
type Drawn =
  | { view: AquariumView; style: 'map'; exhibit: null; hasExhibit: boolean }
  | { view: ExhibitView; style: 'exhibit'; exhibit: Exhibit; hasExhibit: true };

/**
 * The first sanctuary's aquarium in the style asked for, drawn again when a load puts the game at another
 * site or the style changes: as the exhibit only where the site has one (its seafloor and its reef, deeper than
 * the exhibit's last stop, `EXHIBIT_MIN_DEPTH_M`), else as its map (M86's review: a site without one kept the
 * other view's card and button). Nothing is kept until the view is made, so a failure leaves what was drawn
 * (review M18). Its outcrops from the reef's model once it has come (M87).
 */
const drawAquarium = (want: AquariumStyle): Drawn => {
  const first = shown();
  const seafloor = first ? seafloorOf(first) : null;
  const reef = first ? reefOf(first) : null;
  // A site without a reef has its exhibit too (M98: the Cape Peninsula's shelf), deeper than a shelf's last stop.
  const can = seafloor !== null && summarize(seafloor).deepestM > EXHIBIT_MIN_DEPTH_M;
  if (can && want === 'exhibit') {
    const made = exhibitOf(seafloor, reef);
    const names = made.bands.map((b) => (reef ? ISLAND_NAMES[REEF_ISLANDS[b.island] as (typeof REEF_ISLANDS)[number]] : (first?.site.placeName.split(',')[0] ?? '')));
    const view = new ExhibitView(made, names);
    // The habitat kept where the new tank has it, else its first (a visited site's own).
    if (habitat !== 'overview' && habitat !== 'all' && !made.habitats.some((h) => h.id === habitat)) habitat = made.habitats[0]?.id ?? 'overview';
    if (reefModel) view.setReefModel(reefModel.model, reefModel.textures);
    return { view, style: 'exhibit', exhibit: made, hasExhibit: true };
  }
  const view = new AquariumView(seafloor, reef, first?.site.latDeg ?? 0, epochDayOf(world));
  if (reefModel) view.setReefModel(reefModel.model, reefModel.textures);
  return { view, style: 'map', exhibit: null, hasExhibit: can };
};
/** The reef's model and its skins (M87), read once a page; every view built after gets its outcrops from it. */
let reefModel: { model: ReefModel; textures?: ReefTextures } | null = null;
/**
 * The 3D view's own (M288): made by `startGraphics` once every name it touches is declared, and only where the 3D view
 * runs (`gl`); a page without WebGL, or opened with `?render=off`, never makes them, and every use of them is behind
 * `gl` (the page had stopped at the renderer, the HUD never made, and the reef model's arrival had then found the
 * habitat's scene not yet declared: "Cannot access 'oa' before initialization").
 */
let gl = false;
let drawnSite: SiteId | undefined;
let drawn!: Drawn;
let view!: AquariumView | ExhibitView;

/**
 * The reef's model, fetched with the page's own files once the 3D view runs (M87); until it arrives the reef is its
 * floor's tint alone. Laid only on a 3D view still running when it comes (M288: it may have stopped meanwhile).
 */
function loadReefModel(): void {
  void fetch(REEF_MODEL_URL).then((r) => r.arrayBuffer()).then(async (bytes) => {
    if (!gl) return;
    const model = reefModelOf(new Uint8Array(bytes));
    // Its skins apart: a browser that cannot read them still draws the kit, each part in its material's plain color
    // (review M122: a failed image read had dropped the whole kit).
    const textures = await reefTextures(model).catch((error: unknown) => {
      console.error('the reef model\'s skins could not be read', error);
      return undefined;
    });
    if (!gl) return;
    reefModel = { model, textures };
    view.setReefModel(model, textures);
    if (opened?.scene instanceof ReefScene) opened.scene.setReefModel(model, textures);
  }).catch((error: unknown) => console.error('the reef model could not be read', error));
}
/** The world, the day and the view the aquarium's sun was last drawn for. */
let litWorld: World | null = null;
let litDay = -1;
let litView: AquariumView | ExhibitView | null = null;
/**
 * The aquarium's hour, apart from the calendar (M95, the user's brief): Noon until the player chooses another, the
 * light and the animals' daily phase drawn at it while the days run on. Not saved: a way of looking, not the world.
 */
const observing = new ObservationClock();
/** The hour and the day the exhibit's light was last drawn for. */
let litHour = Number.NaN;

/** Draws the day's sun when the world (a load), the day or the view has changed since. */
function lightAquarium(): void {
  const first = shown();
  if (!first || (world === litWorld && world.day === litDay && view === litView)) return;
  litWorld = world;
  litDay = world.day;
  litView = view;
  const day = daySun(first.site.latDeg, epochDayOf(world), first.env);
  view.setDay(day);
  // The end of civil twilight, for the honu's evening (M104's review).
  if (drawn.style === 'exhibit') drawn.view.setDusk(civilDuskHour(first.site.latDeg, solarDeclinationDeg(noonUtc(epochDayOf(world)))));
  // The exhibit's water by its measured clarity and its simulated chlorophyll (M104).
  if (drawn.style === 'exhibit') drawn.view.setWater(day.kdPar, sanctuaryChlorophyll(world, first));
  litHour = Number.NaN;
  const reef = reefOf(first);
  if (reef && first.coral) {
    const cover = columnCovers(reef, first.coral);
    const bleached = columnBleached(reef, first.coral);
    // The coral a heat episode under way has killed, standing dead until it ends (M110).
    const killed = columnKilled(reef, first.coral);
    if (drawn.exhibit) {
      const laid = exhibitCover(reef, cover, bleached, drawn.exhibit.reef, killed);
      view.setReef(laid.cover, laid.bleached, laid.killed);
    } else view.setReef(cover, bleached, killed);
  }
  // The zooplankton through the mixed layer, the exhibit's specks (M97); the humpbacks in Hawaii's waters (M99).
  if (drawn.style === 'exhibit') {
    drawn.view.setPlankton(sanctuaryPlankton(world, first).zoo, first.env.mixedLayerM);
    drawn.view.setWhales(first.whales ? humpbackSprites(whaleCounts(HUMPBACK, first.whales.humpback, epochDayOf(world)), epochDayOf(world)) : { whales: 0, calves: 0 });
    // The honu at home (M104).
    drawn.view.setTurtles(first.turtles ? honuSprites(HONU, first.turtles.honu.home, epochDayOf(world)) : []);
    // The monk seals at home (M145).
    drawn.view.setSeals(first.seals ? sealSprites(MONK_SEAL, first.seals.monk) : []);
  }
  const fish = first.fish;
  if (fish) {
    view.setFish(FISH_KINDS.map((kind) => {
      const life = FISH_LIVES.find((l) => l.id === kind.species) as (typeof FISH_LIVES)[number];
      const c = fishCounts(life, fish[kind.species], epochDayOf(world));
      return { initial: c.adults, terminal: c.terminal, lengthMm: { initial: c.lengthMm.adults, terminal: c.lengthMm.terminal }, byIsland: c.byIsland };
    }));
  }
}

let scene!: Scene;
/** The camera for a view: the map's from its corner, fitted to its sphere; the exhibit's on its habitat (M94). */
const lookAt = (next: Drawn, smooth: boolean): void => {
  scene.setLook(next.style);
  if (next.style === 'exhibit') frameTank(next.view, smooth);
  else {
    scene.camera.position.set(...MAP_LOOK_FROM);
    scene.setFitRadius(next.view.radius);
  }
};

/** Whether what is shown is a habitat (its own scene), not the whole tank. */
function isHabitat(h: Habitat['id'] | 'overview' | 'all'): h is Habitat['id'] {
  return h !== 'overview' && h !== 'all';
}

/** The whole tank framed: its panorama, or all of it after Fit all; its ruler beside it. */
function frameTank(tank: ExhibitView, smooth: boolean): void {
  const frame = tank.frameOf(habitat === 'all' ? 'all' : 'overview');
  tank.rulerAt(frame);
  scene.frame(frame, exhibitLook(frame), smooth);
}

/**
 * The habitats' own scenes (M110): one at a time, drawn by the page's renderer on its canvas with its own camera and
 * controls (`HabitatHost`), the whole tank waiting meanwhile; the day's state laid on it by the adapter, read from
 * the world and never written to it; reached through the water's blue (`Veil`).
 */
let habitatHost!: HabitatHost;
const viewport = document.getElementById('viewport') as HTMLElement;
const veil = new Veil(viewport);
/** The habitat scene shown, or null for the whole tank; the world and the day it was last given and when (`relayDue`), and the hour and the depth it was last lit at. */
let opened: OpenedHabitat | null = null;
let openedFor: { world: World; day: number; at: number } | null = null;
let openedLit = { hour: Number.NaN, depthM: Number.NaN };

/** Opens a habitat's own scene, the one before freed; false where it cannot be (the map drawn, or a reef the site has none of). */
function showHabitat(id: Habitat['id']): boolean {
  const place = shown();
  if (!place || drawn.style !== 'exhibit') return false;
  const box = drawn.view.habitats.find((h) => h.id === id);
  if (!box) return false;
  const next = openHabitat(world, place, box, drawn.exhibit, reefModel);
  if (!next) return false;
  // The reef as an island's own where one was chosen from its card (M287), else the whole of Maui Nui's.
  next.setFocus?.(reefFocus, world, place);
  if (reefThen) next.setThen?.(firstDayOf(), world, place);
  closeHabitat();
  opened = next;
  openedFor = { world, day: world.day, at: performance.now() };
  openedLit = { hour: Number.NaN, depthM: Number.NaN };
  habitatHost.open(next.scene, next.key);
  // While a watched day holds the camera, a habitat opens at its home view, as the lock holds it (the review's: it had
  // opened at the player's view of it and been held there).
  const locked = observing.watching?.locked ?? false;
  if (locked) habitatHost.resetView();
  habitatHost.controls.enabled = !locked;
  scene.controls.enabled = false;
  hud.setScene(true);
  hud.setDrawingNote(next.note(), '', false);
  return true;
}

/** The island whose reef the Reef scene shows as its own (M287), or none for the whole of Maui Nui. */
let reefFocus: ReefIsland | null = null;
/** Whether the Reef shows the first day's reef in place of today's (M287). */
let reefThen = false;

/**
 * The reef on the player's first day (M287's before-and-after, `docs/CONSERVATION_PROGRESS.md` 6.4): the same game
 * replayed to its start from its seed, opening and date, as the saves' migrations replay a world (`save.ts`); built once
 * a game and kept. Not the saved past, which the game does not keep: the start, which it can rebuild exactly.
 */
let firstDay: { for: World; world: World; place: Place } | null = null;
function firstDayOf(): { world: World; place: Place } | null {
  const own = world.sanctuaries[0];
  if (!own) return null;
  if (firstDay?.for !== world) {
    const start = createWorld({ seed: world.seed, opening: own.siteId, startDate: civilFromEpochDay(world.startEpochDay) });
    const place = shownPlace(start, null);
    if (!place) return null;
    firstDay = { for: world, world: start, place };
  }
  return firstDay;
}

/** The Reef as it was on the first day, or as it is today (M287), an island's focus kept. */
function chooseReefThen(on: boolean, island: ReefIsland | null): string {
  // Without the 3D view (M288) the first day is not drawn; its measurements are the island's chart's first points.
  if (!gl) {
    hud.say(on ? 'The reef on your first day is drawn in the 3D view alone; its measurements are the earliest points on each island\'s chart.' : 'The reef today.');
    return habitat;
  }
  reefFocus = island;
  reefThen = on;
  hud.say(on ? `The reef on your first day here${island ? `, ${ISLAND_NAMES[island]}'s` : ''}, rebuilt from the game's seed. Compare it with today's.` : `The reef today${island ? `, ${ISLAND_NAMES[island]}'s` : ''}.`);
  const place = shown();
  if (opened?.habitat === 'reef' && place && opened.setFocus) {
    opened.setFocus(island, world, place);
    opened.setThen?.(on ? firstDayOf() : null, world, place);
    hud.showAquarium();
    hud.setDrawingNote(opened.note(), '', false);
    return habitat;
  }
  return chooseHabitat('reef', true);
}

/** The Reef scene opened as an island's own, from its card (M287): its coral, algae and fish that island's. */
function chooseReefIsland(island: ReefIsland): string {
  reefThen = false;
  reefFocus = island;
  hud.say(`${ISLAND_NAMES[island]}'s reef: its own coral, seaweed and fish as they are now.`);
  // Without the 3D view (M288) the substitute shows the island's reef.
  if (!gl) {
    habitat = 'reef';
    hud.showAquarium();
    return habitat;
  }
  const place = shown();
  if (opened?.habitat === 'reef' && place && opened.setFocus) {
    opened.setFocus(island, world, place);
    opened.setThen?.(null, world, place);
    hud.showAquarium();
    hud.setDrawingNote(opened.note(), '', false);
    return habitat;
  }
  return chooseHabitat('reef', true);
}

/** The exhibit's camera taken to a habitat's own scene, or the whole tank (M94, M110); the one it is on. */
function chooseHabitat(id: string, focused = false): string {
  // A habitat chosen from Explore shows the island in focus, or the whole reef where none is (M292; M287 had given an
  // island's focus up here, the card still on that island).
  if (!focused) { reefFocus = null; reefThen = false; }
  // Without the 3D view (M288) the substitute shows the habitat (or the whole tank's), the place's own.
  if (!gl) {
    unfollow();
    onwardAsked = null;
    habitat = substituteChoices().some((c) => c.id === id) ? (id as Habitat['id']) : 'overview';
    hud.showAquarium();
    return habitat;
  }
  if (drawn.style !== 'exhibit') return habitat;
  unfollow();
  // A way on asked for and not yet taken given up for this choice (M147's review: one left over was taken later, unasked).
  onwardAsked = null;
  // A habitat chosen during a watched day: the camera the player's, not given back (M104).
  observing.cameraMoved();
  const known = drawn.view.habitats.find((h) => h.id === id);
  // Its own scene, or the whole tank, through the water's blue (M110): never the camera travelling down the tank; on a
  // phone the sheet folded, the aquarium seen whole (M117).
  habitat = known ? known.id : 'overview';
  const chosen = habitat;
  veil.through(() => {
    // Another choice made while the view was covered (Fit all, which does not go through the veil) its own: the swap
    // left undone (review M122: Fit all within its 0.22 s opened the habitat anyway, Fit all still lit).
    if (habitat !== chosen) return;
    // The graphics stopped while the view was covered: the substitute shows the choice (review 33: the lost view's
    // scene was asked for).
    if (!gl) { chooseHabitat(chosen, true); return; }
    // Folded under the veil, so the scene left is not seen refitted to the screen it gains.
    hud.showAquarium();
    if (!known) showTank();
    else if (!showHabitat(known.id)) {
      onwardAsked = null;
      habitat = 'overview';
      showTank();
      hud.setHabitats(drawn.style === 'exhibit' ? drawn.view.habitats : null, habitat);
    } else sayScarcity();
  });
  return habitat;
}

/**
 * A reef school's card, or a humpback's in the Blue Water (M210), with the way on where one of its kind crossed today to
 * a habitat it visits (M147): the latest to have gone by the hour, followed there as the visitor come in place of one
 * gone home.
 */
function withCrossing(card: WildlifeCard, species: VisitorSpecies, place: Place, hour: number): WildlifeCard {
  if (drawn.style !== 'exhibit') return card;
  if (crossings?.world !== world || crossings.day !== world.day) crossings = { world, day: world.day, byHabitat: new Map() };
  for (const use of SPECIES_VISITS[species].visits) {
    if (!drawn.view.habitats.some((h) => h.id === use.habitat)) continue;
    let day = crossings.byHabitat.get(use.habitat);
    if (!day) {
      day = speciesVisits(world, place, epochDayOf(world), use.habitat).visitors;
      crossings.byHabitat.set(use.habitat, day);
    }
    const went = day.filter((v) => v.kind === species && v.slot?.after && hour >= v.slot.hour).sort((a, b) => (a.slot?.hour ?? 0) - (b.slot?.hour ?? 0)).pop();
    if (!went?.slot) continue;
    onward = { habitat: use.habitat, then: { visitor: visitorId(went) } };
    const there = HABITAT_NAMES[use.habitat] ?? use.habitat;
    const whose = species === 'humpback' ? 'the humpbacks in Hawaiʻi\'s waters' : `Maui Nui's ${SPECIES_NAMES[species].toLowerCase()}`;
    return { ...card, rows: [...card.rows, ['Crossing', `one of ${whose} swam to ${there} at ${clockLabel(went.slot.hour)} today, as one there swam back in its place`]], onward: `Follow it to ${there}` };
  }
  return card;
}

/** Where the reef shown holds few of the simulated fish, said so as it opens (`ReefScene.scarcity`, M117). */
function sayScarcity(): void {
  const note = opened?.scene instanceof ReefScene ? opened.scene.scarcity() : null;
  if (note) hud.say(note);
}

/** Frees the habitat's scene, if one is shown: the whole tank's turn again. */
function closeHabitat(): void {
  if (!opened) return;
  unfollow();
  habitatHost.close();
  opened = null;
  scene.controls.enabled = !(observing.watching?.locked ?? false);
  hud.setScene(false);
  hud.setDrawingNote(view.scaleNote, drawn.style === 'exhibit' ? exhibitChoices() : '');
}

/** The whole tank again, framed as it was left (its panorama, or all of it). */
function showTank(): void {
  if (!gl) return;
  closeHabitat();
  if (drawn.style === 'exhibit') frameTank(drawn.view, false);
}

function redrawAquariumIfMoved(): void {
  if (shown()?.siteId === drawnSite) return;
  redrawAquarium();
}

function redrawAquarium(want: AquariumStyle = chosen): void {
  // Without the 3D view (M288) nothing is drawn: the site noted, the substitute following `shown()`.
  if (!gl) {
    drawnSite = shown()?.siteId;
    return;
  }
  // The new view first, so a failure leaves the old one drawn, its style with it (review M18).
  const next = drawAquarium(want);
  // A school followed is let go with the view it swam in (M97); a watched day's camera is the old view's, not given
  // back to the new one (M104's review).
  unfollow();
  observing.cameraMoved();
  closeHabitat();
  scene.scene.remove(view.group);
  view.dispose();
  const restyled = next.style !== drawn.style;
  chosen = want;
  drawn = next;
  view = next.view;
  drawnSite = shown()?.siteId;
  scene.scene.add(view.group);
  if (restyled || next.style === 'exhibit') lookAt(next, false);
  else scene.setFitRadius(view.radius);
  hud.setDrawingNote(view.scaleNote, next.style === 'exhibit' ? exhibitChoices() : '');
  hud.setView(next.style, next.hasExhibit);
  // The habitat's own scene again at the new site, where it has that habitat (`drawAquarium` kept it, or took its first).
  if (next.style === 'exhibit' && isHabitat(habitat)) {
    if (!showHabitat(habitat)) habitat = 'overview';
    else sayScarcity();
  }
  hud.setHabitats(next.style === 'exhibit' ? next.view.habitats : null, habitat);
}

/** A load or a new game: play goes on in `next`, the accumulator emptied, the HUD redrawn and the aquarium if its site moved. */
function replaceWorld(next: World): void {
  // A day being watched ends with the world it showed, the speed it held given back (M96).
  if (observing.watching) stopWatching();
  // A school followed, and the days watched for its trend, are the old world's (M97); a visit ends, the player's own
  // aquarium shown (M98).
  unfollow();
  // A way on asked for in the old world is not taken in the new (review M149, as a visit's and a habitat's choice clear it).
  onwardAsked = null;
  visiting = null;
  // An advance under way is the old world's (M240's review: a New game or a Load ran the new world on to the old one's day).
  advancing = null;
  monthShown = monthKey(next);
  seasonTold = seasonKey(next);
  hud.dropMonth();
  seen.clear();
  seenDay = -1;
  // An island's reef and the first day asked for are the old game's (M287's review): the Reef laid again as the new
  // world's whole reef today.
  if (reefFocus !== null || reefThen) {
    reefFocus = null;
    reefThen = false;
    const place = shownPlace(next, visiting);
    if (opened?.habitat === 'reef' && place) {
      opened.setThen?.(null, next, place);
      opened.setFocus?.(null, next, place);
    }
  }
  world = next;
  accumulator = 0;
  // The island in focus is the old game's (M292): the new one opens on the whole sanctuary.
  hud.chooseIsland(null);
  hud.invalidate();
  redrawAquariumIfMoved();
  showVisit();
}

/** The last closed month as a key (its year and month), and the one whose summary was shown or stood at the start. */
const monthKey = (w: World): number => { const m = w.operations.months.at(-1); return m ? m.year * 12 + m.month : 0; };
let monthShown = monthKey(world);

/**
 * This year's warm season as last told (M323, the casual year's H5; `seasonKey`): when it is decided (a marine heatwave,
 * or the season passing without one), its result's card, as a 'good' event's (M320), whatever came, each year.
 */
let seasonTold = seasonKey(world);
function seasonDecided(): GameEvent[] {
  const news = seasonNews(world, seasonTold);
  seasonTold = seasonKey(world);
  return news ? [news] : [];
}

let speed: SpeedSetting = 1;
let lastNonZeroSpeed: SpeedSetting = 1;
/** The speed a day being watched held the simulation from (M96), given back when it ends. */
let speedBeforeWatch: SpeedSetting = 1;

/**
 * A speed the player presses, on the bar or the keys: it ends a day being watched, and the simulation runs at their
 * speed, not the held one (M96, the brief: "unless the player explicitly chooses otherwise").
 */
function pressSpeed(next: SpeedSetting): void {
  if (observing.watching && next !== PAUSED) {
    // The camera freed with the day it was locked for, and given back where the player left it (M104).
    freeCamera();
    giveBack(observing.stopWatching(shown()?.env.dayLengthHours ?? 12));
  }
  // Pause pressed during a watch is the player's own: kept when the watch ends (M96's review).
  if (observing.watching) speedBeforeWatch = PAUSED;
  // A speed or Pause pressed ends an advance under way (M240's review: only its own button had stopped it).
  advancing = null;
  setSpeed(next);
}

/** Ends Watch One Day: its preset and the speed it held given back (M96). */
/**
 * The camera locked on what it frames for the rest of a watched day, or freed (M104, the brief's section 13.3: "lock
 * the camera on a habitat or animal for the full 90 seconds"): an animal followed stays followed; else the habitat
 * chosen is framed again; the player's turning and zooming are held until it is freed or the day is left.
 */
function lockCamera(locked: boolean): void {
  if (!gl) return;
  observing.setLocked(locked);
  // A habitat's scene held at its own home view (M110).
  if (opened) {
    habitatHost.controls.enabled = !locked;
    if (locked && following === null) habitatHost.resetView();
    return;
  }
  scene.controls.enabled = !locked;
  // Eased back to the habitat's frame (M104's review: through `lookAt` the look's reset placed it at once).
  if (locked && following === null && drawn.style === 'exhibit') frameTank(drawn.view, true);
}

/** The camera shown given back to the player's turning and zooming: a habitat scene's, or the whole tank's. */
function freeCamera(): void {
  if (!gl) return;
  if (opened) habitatHost.controls.enabled = true;
  else scene.controls.enabled = true;
}

/** The camera's view from before a watched day given back as it ends, where the player left it there and follows nothing (M104). */
function giveBack(view: CameraView | null): void {
  if (!gl || !view || following !== null) return;
  if (opened) habitatHost.moveTo(view);
  else scene.moveTo(view);
}

function stopWatching(): void {
  freeCamera();
  giveBack(observing.stopWatching(shown()?.env.dayLengthHours ?? 12));
  // Ended behind the open menu (a new game, a load), the speed given back as the menu closes.
  if (hud.menuOpen) hud.setSpeedAfterMenu(speedBeforeWatch);
  else setSpeed(speedBeforeWatch);
}

/** The commands the world has taken this session, so the autosave sees a change made on its day (M238). */
let commandsApplied = 0;
const hud = new Hud(hudRoot, {
  // Advance to the next event (M240): the clock paused, the days stepped to it; pressed again, stopped.
  nextEvent: () => {
    if (advancing) { advancing = null; return; }
    const e = nextEvent(world);
    if (!e) return;
    // Said, not silent, during a watched day (review 29).
    if (observing.watching) { hud.say('The day is being watched: stop watching, then the days can run on.'); return; }
    pressSpeed(PAUSED);
    advancing = { target: e.day, what: e.what };
  },
  // Advance to a given day (M262: the objective's "Advance to its return"), as the next event's advance runs.
  advanceTo: (day, what) => {
    if (observing.watching) { hud.say('The day is being watched: stop watching, then the days can run on.'); return; }
    if (day <= epochDayOf(world)) return;
    pressSpeed(PAUSED);
    advancing = { target: day, what };
  },
  setSpeed: pressSpeed,
  command: (command) => {
    // A tag given from a school or an octopus followed names that one, its mark over it, kept with the save (M203; the
    // session's memory before, M156 and M201); an animal tagged in a habitat's scene stays there the rest of the day.
    const seenAs = following === null ? undefined : 'school' in following ? following.school : 'octopus' in following ? following.octopus : undefined;
    // The habitat it was tagged in: the scene's, or without the 3D view the substitute's (M288).
    const shownIn: string | undefined = gl ? opened?.habitat : isHabitat(habitat) ? habitat : undefined;
    const seenIn = shownIn !== undefined && (TAG_HABITATS as readonly string[]).includes(shownIn) ? (shownIn as TagHabitat) : undefined;
    const given = command.type === 'tag_animal' ? { ...command, ...(seenAs !== undefined ? { seenAs } : {}), ...(seenIn ? { seenIn } : {}) }
      : command.type === 'tag_group' && seenAs !== undefined ? { ...command, seenAs } : command;
    const result = applyCommand(world, given);
    // The world changed on its day: the next autosave writes it (review M238's: a paused game's commands were not saved).
    if (result.ok) commandsApplied++;
    // Without the 3D view (M288's review), a tag made puts its card away: the animal is now the tagged one the panel lists,
    // not one to tag again (the 3D view's card turns to the tagged animal's, below).
    if (result.ok && !gl && (command.type === 'tag_animal' || command.type === 'tag_group')) {
      offeredDay = null;
      hud.setWildlife(null);
    }
    // A habitat's visitor tagged while followed: followed on as its tag's own, drawn as itself from the scene's next day's
    // state, given now (M203).
    if (result.ok && command.type === 'tag_animal' && following !== null && 'visitor' in following) {
      const made = world.tags.animals[world.tags.animals.length - 1];
      if (made) {
        // Its drawing handed over to its tag's at once, its place kept (M203's second review: left to the next day's
        // state, a relay behind at speed, the one tapped swapped for another).
        opened?.scene.handOverVisitor(following.visitor, `tag:${made.id}`, { id: made.id, name: made.name });
        following = { visitor: `tag:${made.id}` };
        openedFor = null;
      }
    }
    hud.invalidate();
    return result;
  },
  save: () => writeSlot(QUICK_SLOT, world),
  load: () => {
    const loaded = readSlot(QUICK_SLOT);
    if (typeof loaded === 'string') return loaded;
    replaceWorld(loaded);
    holdPaused();
    return 'Loaded, paused. Press Play to run on.';
  },
  newGame: () => {
    replaceWorld(freshWorld());
    // Paused as the first page opens (M319, below).
    holdPaused();
    return 'A new game, paused: the days run when you press Play or an Advance.';
  },
  switchView: () => {
    if (!gl) return 'exhibit';
    redrawAquarium(drawn.style === 'map' ? 'exhibit' : 'map');
    return drawn.style;
  },
  observe: (preset) => {
    // The hour the 3D view is drawn at: nothing to draw without it (M288).
    if (!gl) { hud.say(SUBSTITUTE_LIMITS); return observing.preset; }
    if (observing.watching) stopWatching();
    observing.choose(preset, shown()?.env.dayLengthHours ?? 12);
    return observing.preset;
  },
  watch: (action) => {
    // Watch One Day is the 3D view's (M288).
    if (!gl) { hud.say(SUBSTITUTE_LIMITS); return; }
    if (action === 'start' || action === 'replay') {
      // The speed the simulation ran at, held at pause while the day is watched and given back after (M96).
      if (!observing.watching) speedBeforeWatch = speed;
      // A watch ends an advance under way (M240's second review: held through the watch, it ran on once the watch ended).
      advancing = null;
      setSpeed(PAUSED);
      observing.watch(opened ? habitatHost.view() : scene.view());
    } else if (action === 'pause') observing.setPlaying(false);
    else if (action === 'resume') observing.setPlaying(true);
    else if (action === 'lock' || action === 'unlock') lockCamera(action === 'lock');
    else stopWatching();
  },
  watchLength: (seconds) => {
    observing.setLength(seconds);
    return observing.length;
  },
  unfollow: () => unfollow(),
  goToTag: (id) => {
    // A tagged animal gone to in the habitat its tag tells it is in today (M203), and followed there as itself; where it
    // cannot be, why (`goToOf`).
    const go = goToOf(world, id, epochDayOf(world), { visiting: visiting !== null, habitats: gl ? drawn.style === 'exhibit' : substituteChoices().length > 0 });
    if ('why' in go) return go.why;
    // Without the 3D view (M288) its habitat shown in the substitute, where its tag puts it today.
    if (!gl) {
      hud.pickHabitat(go.habitat);
      return null;
    }
    if (drawn.style !== 'exhibit') return null;
    chooseHabitat(go.habitat);
    hud.setHabitats(drawn.view.habitats, habitat);
    onwardAsked = { habitat: go.habitat, then: { visitor: `tag:${id}` } };
    return null;
  },
  followOnward: () => {
    const way = onward;
    if (!way || !gl || drawn.style !== 'exhibit') return;
    chooseHabitat(way.habitat);
    hud.setHabitats(drawn.view.habitats, habitat);
    onwardAsked = way;
  },
  visit: (siteId) => {
    // A site of the seven visited from the world map, or the player's own again (M98): a way of looking, the world
    // untouched; the aquarium drawn afresh, the old one freed.
    visiting = siteId;
    unfollow();
    onwardAsked = null;
    redrawAquarium();
    showVisit();
  },
  chooseHabitat: (id) => chooseHabitat(id),
  chooseReefThen: (on, island) => chooseReefThen(on, island),
  fitAll: () => {
    if (!gl || drawn.style !== 'exhibit') return habitat;
    unfollow();
    onwardAsked = null;
    // Its own frame, so a lock or a redraw keeps all of it (M105's review: as the whole tank's, a lock framed the
    // panorama).
    habitat = 'all';
    closeHabitat();
    frameTank(drawn.view, true);
    observing.cameraMoved();
    return habitat;
  },
  resetView: () => {
    if (!opened) return;
    unfollow();
    observing.cameraMoved();
    habitatHost.resetView();
  },
  frameMeterOn: () => frameMeter.visible,
  devMode: () => devMode,
  toggleFrameMeter: () => {
    frameMeter.setVisible(!frameMeter.visible);
    try {
      localStorage.setItem(FRAME_METER_KEY, frameMeter.visible ? 'on' : 'off');
    } catch {
      // Storage blocked: the choice holds for this visit and no longer.
    }
    return frameMeter.visible;
  },
});

/** Whether the browser has taken the canvas's graphics away (a phone short of memory), as Sable Reserve's watches (M166). */
let graphicsLost = false;
canvas.addEventListener('webglcontextlost', () => {
  graphicsLost = true;
});
canvas.addEventListener('webglcontextrestored', () => {
  graphicsLost = false;
});

// The frame rate meter (M166, the user's word: "show a framerate display at the top of the screen like for safari
// sim"), Sable Reserve's: under the bars, on unless the player turned it off from the menu, their choice kept.
// A developer's alone since M237 (the overhaul's brief: "Restrict FPS and performance diagnostics to developer mode").
/**
 * The developer's hunts (M251, the brief's section 11): `seaHunts.trigger('roi', 'palenose', true)` and the like in the
 * console, a strike begun on the Reef at once where such a pair is in view, counting nothing; and the overlay of the
 * strikes under way (`?huntoverlay`, or `seaHunts.overlay()`; `?hunts=N` the budget beside it). Neither in normal play.
 */
const huntOverlay = devMode ? mountHuntOverlay(hudRoot, new URLSearchParams(location.search).has('huntoverlay')) : null;
if (devMode) {
  (window as unknown as { seaHunts: unknown }).seaHunts = {
    trigger: (predator: string, prey: string | null = null, success = true): boolean =>
      opened?.scene instanceof ReefScene ? opened.scene.triggerHunt(Math.max(1, canvas.clientHeight), predator, prey, success) : false,
    overlay: (on = true): void => huntOverlay?.toggle(on),
  };
}
/**
 * The developer's interactions (M265, the interactions brief's section 13): `seaLife.trigger('graze')` and the rest
 * (`ReefScene.triggerLife`'s names) in the console, begun on the Reef at once where one can be, counting nothing; and the
 * overlay of those under way (`?lifeoverlay`, or `seaLife.overlay()`). Neither in normal play.
 */
const lifeOverlay = devMode ? mountLifeOverlay(hudRoot, new URLSearchParams(location.search).has('lifeoverlay')) : null;
if (devMode) {
  (window as unknown as { seaLife: unknown }).seaLife = {
    trigger: (name: string): boolean => (opened?.scene instanceof ReefScene ? opened.scene.triggerLife(name) : false),
    overlay: (on = true): void => lifeOverlay?.toggle(on),
  };
}
const frameMeter = mountFrameMeter(hudRoot, devMode && ((): boolean => {
  try {
    return frameMeterShown(localStorage.getItem(FRAME_METER_KEY));
  } catch {
    return true;
  }
})());
// The player's own turn or zoom during a watched day: the camera theirs, not given back (M104); a tap that moves
// nothing is not one.
const flat = (v: CameraView): number[] => [...v.position, ...v.target];
let grabbed: number[] | null = null;
let grabbedHabitat: number[] | null = null;
/** The cameras' own turns and zooms watched (M104, M110), once the 3D view's cameras are made. */
function watchCameras(): void {
  // Only the controls in use: the others take a finger's lost lift too (`pointerHygiene.ts`), with a start and an end that
  // are no player's (M115's review).
  scene.controls.addEventListener('start', () => { if (scene.controls.enabled) grabbed = flat(scene.view()); });
  scene.controls.addEventListener('end', () => {
    const was = scene.controls.enabled ? grabbed : null;
    if (was && flat(scene.view()).some((v, i) => Math.abs(v - (was[i] as number)) > 1e-3)) {
      observing.cameraMoved();
      // The habitats' dropdown no longer on a habitat, so any can be chosen again (M105's review).
      hud.habitatLeft();
    }
    grabbed = null;
  });
  // The same for a habitat scene's own controls (M110), its dropdown kept on the habitat: the player is still in it.
  habitatHost.controls.addEventListener('start', () => { if (habitatHost.controls.enabled) grabbedHabitat = flat(habitatHost.view()); });
  habitatHost.controls.addEventListener('end', () => {
    const was = habitatHost.controls.enabled ? grabbedHabitat : null;
    if (was && flat(habitatHost.view()).some((v, i) => Math.abs(v - (was[i] as number)) > 1e-3)) observing.cameraMoved();
    grabbedHabitat = null;
  });
}

/**
 * The 3D view started (M288): the aquarium drawn, its renderer and its habitats' host made, its cameras watched, and
 * the page opened on the reef's own scene where the site has one (M110), else on the whole tank; the reef's model then
 * fetched. Where any of it fails (no WebGL: "Error creating WebGL context"), what was made is freed and the game goes on
 * without it, its substitute in the ocean's place.
 */
function startGraphics(): void {
  try {
    drawnSite = shown()?.siteId;
    drawn = drawAquarium(chosen);
    view = drawn.view;
    scene = createScene(canvas, view.radius);
    habitatHost = new HabitatHost(canvas);
    lookAt(drawn, false);
    scene.scene.add(view.group);
    watchCameras();
    gl = true;
    hud.setView(drawn.style, drawn.hasExhibit);
    if (drawn.style === 'exhibit' && isHabitat(habitat)) {
      if (!showHabitat(habitat)) habitat = 'overview';
      else sayScarcity();
    }
    hud.setHabitats(drawn.style === 'exhibit' ? drawn.view.habitats : null, habitat);
    if (!opened) hud.setDrawingNote(view.scaleNote, drawn.style === 'exhibit' ? exhibitChoices() : '');
  } catch (error) {
    console.error('the 3D view could not start', error);
    freeGraphics();
    startSubstitute(NO_3D_NOTICE);
    return;
  }
  loadReefModel();
}

/** The 3D view's resources freed, as much of them as were made, each on its own so one failing frees the rest (M288). */
function freeGraphics(): void {
  gl = false;
  // A watched day ends with the view it was watched in (M288's review: its bar hidden, the days held with no way on).
  if (observing.watching) stopWatching();
  following = null;
  onward = null;
  onwardAsked = null;
  const wasOpen = opened !== null;
  const steps: (() => void)[] = [
    () => { if (wasOpen) habitatHost?.close(); },
    () => habitatHost?.controls.dispose(),
    () => view?.dispose(),
    () => scene?.controls.dispose(),
    () => scene?.renderer.dispose(),
  ];
  opened = null;
  hud.setWildlife(null);
  for (const step of steps) {
    try {
      step();
    } catch (error) {
      console.error('a part of the 3D view could not be freed', error);
    }
  }
  canvas.style.display = 'none';
}

/** The 3D view stopped while playing (a drawing error, M288): freed, the game going on with its substitute. */
function stopGraphics(error: unknown): void {
  console.error('the 3D view stopped', error);
  freeGraphics();
  startSubstitute(STOPPED_3D_NOTICE);
}

/** The substitute for the 3D view (M288), its notice and the habitats it offers at the place shown. */
let substitute: ViewSubstitute | null = null;
let substituteNotice = '';
let substituteHabitats: { siteId: SiteId; choices: SubstituteChoice[] } | null = null;
let substituteHud = '';
/** The day a tag's card was offered by the substitute, or none: its animal is that day's. */
let offeredDay: number | null = null;

/** The place's habitats as the 3D view's exhibit holds them (`exhibitOf`, its depths `depthsOf`), none where it has no exhibit; once a site. */
function substituteChoices(): SubstituteChoice[] {
  const place = shown();
  if (!place) return [];
  if (substituteHabitats?.siteId !== place.siteId) {
    const seafloor = seafloorOf(place);
    const made = seafloor !== null && summarize(seafloor).deepestM > EXHIBIT_MIN_DEPTH_M ? exhibitOf(seafloor, reefOf(place)) : null;
    substituteHabitats = { siteId: place.siteId, choices: made ? made.habitats.map((h) => ({ id: h.id, name: h.name, depthRangeM: depthsOf(h, made) })) : [] };
  }
  return substituteHabitats.choices;
}

/**
 * The game without its 3D view (M288): the canvas hidden, the controls only it uses taken away (`Hud.setGraphics`), the
 * substitute in the ocean's place, and the player told once, in a notice that blocks nothing.
 */
function startSubstitute(notice: string): void {
  substituteNotice = notice;
  canvas.style.display = 'none';
  hud.setGraphics(false);
  hud.setView('exhibit', false);
  hud.setScene(false);
  hud.setDrawingNote(null, '', false);
  substitute ??= new ViewSubstitute(viewport, {
    habitat: (id) => hud.pickHabitat(id),
    island: (island) => {
      if (island) chooseReefIsland(island);
      else reefFocus = null;
      // Manage's card follows the island chosen in the view (M292).
      hud.chooseIsland(island);
    },
    // A tag's card the panel offers (`viewSubstitute.ts`), each made with its tag, shown in the HUD's own card; the
    // day's own, gone with it (`substituteFrame`).
    card: (offered) => {
      offeredDay = world.day;
      hud.setWildlife(offered);
    },
  });
  hud.say(notice);
}

/** A frame of the substitute: its habitats kept the place's, the HUD's strip with them, and the panel drawn as it changes. */
function substituteFrame(now: number, hour: number): void {
  const place = shown();
  if (!substitute || !place) return;
  const choices = substituteChoices();
  // The habitat kept where the place has it, else its first (a visited site's own), as `drawAquarium` keeps it.
  if (isHabitat(habitat) && !choices.some((c) => c.id === habitat)) habitat = (choices[0]?.id as Habitat['id'] | undefined) ?? 'overview';
  const strip = `${place.siteId}|${habitat}`;
  if (strip !== substituteHud) {
    substituteHud = strip;
    hud.setHabitats(choices.length > 0 ? choices : null, isHabitat(habitat) ? habitat : 'overview');
  }
  // A tag's card offered is its day's animal: put away as the day turns (M288's review: yesterday's visitor tagged today).
  if (offeredDay !== null && offeredDay !== world.day) {
    offeredDay = null;
    hud.setWildlife(null);
  }
  substitute.setInsets(hud.insets());
  substitute.show(world, place, choices, { habitat: isHabitat(habitat) ? habitat : 'overview', island: habitat === 'reef' ? reefFocus : null, hour }, substituteNotice, commandsApplied, now);
}

/**
 * Follow Wildlife (M97, the brief's sections 5 and 8): a school the player taps in the exhibit, by its seed, or a
 * humpback by its place among the whales drawn (M99), the camera kept on it and its card shown until it is let go,
 * its habitat left or its animals gone.
 */
// Every animal drawn can be followed, on every screen (M152, the user's word: "i should be able to select and follow
// every creature on every screen"): a school, a whale, a honu or a seal in the whole tank, a habitat's day's visitor.
/**
 * A tagged animal followed and not drawn in the habitat shown (M203): kept followed where its tag has it there on the
 * scene's day (its drawing on its way, a relay behind the world at speed: M203's second review); where it is in another
 * habitat that day, its card saying where, with the way on to it there (`movedTo`); null where it is in none.
 */
function movedTag(id: string, here: string): { card: WildlifeCard | null; onward: { habitat: string; then: { visitor: string } } | null } | null {
  const day = openedFor ? openedFor.world.startEpochDay + openedFor.day : epochDayOf(world);
  const at = movedTo(world, id, here, day);
  if (at === 'here') return { card: null, onward: null };
  const a = world.tags.animals.find((t) => `tag:${t.id}` === id);
  if (!at || !a || drawn.style !== 'exhibit' || !drawn.view.habitats.some((h) => h.id === at)) return null;
  const there = HABITAT_NAMES[at] ?? at;
  return {
    card: { title: a.name, rows: [['Moved', `${a.name} is in ${there} today, as its tag tells it`]], onward: `Follow it to ${there}` },
    onward: { habitat: at, then: { visitor: id } },
  };
}

/** A card with what a tag of its animal would take (M148), at the player's own sanctuary only, not a visited one's, nor for one tagged already (M203). */
function tagging(card: WildlifeCard, tag: NonNullable<WildlifeCard['tag']>, tagged = false): WildlifeCard {
  return visiting === null && !tagged ? { ...card, tag } : card;
}
let following: { school: number; fish?: string } | { whale: number } | { honu: { adult: boolean; index: number } } | { seal: number } | { visitor: string } | { octopus: number } | { bottomfish: string } | { dolphin: string } | null = null;
/**
 * The way on with the animal followed into the habitat it crosses to (M147, the user's word: "allow players to sometimes
 * follow an animal into a different habitat if it happens to be travelling there"): the habitat, and there the visitor
 * come in its place or the school of its kind nearest the view; and one asked for, followed once that habitat is open.
 */
let onward: { habitat: string; then: { visitor: string } | { species: VisitorSpecies } } | null = null;
let onwardAsked: { habitat: string; then: { visitor: string } | { species: VisitorSpecies } } | null = null;

/**
 * The tagged animals and named schools marked in a view (M156, M148's split: the user's choice "marked in the views"):
 * each living tag at home and each named school over what stands for it there; none at a visited site.
 */
function showMarks(view: { marks: TagMarks; markAt(target: MarkTarget, remembered: number | null): THREE.Vector3 | null }, camera: THREE.Camera, canvasPx: number): void {
  const place = shown();
  const targets = place && !place.visiting ? tagTargets(world.tags, place.siteId, epochDayOf(world)) : [];
  view.marks.set(targets.map((t) => ({ text: t.text, at: view.markAt(t.target, rememberedFor(t.target)) })), camera, canvasPx);
}

/** The day's crossings into each habitat by the species visiting it, each day's once (M147). */
let crossings: { world: World; day: number; byHabitat: Map<string, Visitor[]> } | null = null;
/** Each species' adults over the days this session has watched, for the card's trend (not saved: the world keeps no history). */
const seen = new Map<FishSpeciesId, Seen>();
let seenDay = -1;

/** The visit's note over the aquarium (M98): what a visited site is and is not. */
function showVisit(): void {
  const place = shown();
  const home = world.sanctuaries[0]?.name.replace(/ Sanctuary$/, '') ?? 'your sanctuary';
  hud.setVisit(place?.visiting ? {
    name: place.name.replace(/ Sanctuary$/, ''),
    note: 'not one of your sanctuaries. Its seafloor is real (GEBCO); its water, light and plankton are its region\'s; no wildlife is simulated here yet, so none is drawn; its kelp is not drawn, as no source on it could be read.',
  } : null, home);
}

function unfollow(): void {
  following = null;
  onward = null;
  if (gl) {
    scene.follow(null);
    habitatHost.follow(null);
  }
  hud.setWildlife(null);
}

/** A tap, not a drag: a pointer let go within 6 px and half a second of where and when it went down. */
let pressed: { x: number; y: number; t: number } | null = null;
canvas.addEventListener('pointerdown', (event) => { pressed = { x: event.clientX, y: event.clientY, t: performance.now() }; });
canvas.addEventListener('pointerup', (event) => {
  const was = pressed;
  pressed = null;
  if (!gl) return;
  // None while the view is covered for a swap (review M122: a tap on the tank in the fade followed a school of the
  // scene opened behind it, the two numbering their schools alike).
  if (!was || veil.covering() || Math.hypot(event.clientX - was.x, event.clientY - was.y) > 6 || performance.now() - was.t > 500) return;
  const r = canvas.getBoundingClientRect();
  const x = ((event.clientX - r.left) / r.width) * 2 - 1;
  const y = -(((event.clientY - r.top) / r.height) * 2 - 1);
  // In a habitat's own scene its own animals (M110): a school of the reef's, a whale of the open water's.
  if (opened) {
    const picked = opened.scene.pick(x, y, r.width, r.height, 24);
    if (!picked) return;
    following = picked;
    observing.cameraMoved();
    habitatHost.follow(null);
    return;
  }
  // The map's own fish (M152: every creature on every screen), its only animals.
  if (drawn.style === 'map') {
    const seed = drawn.view.pickSchool(scene.camera, x, y, r.width, r.height, 24);
    if (seed < 0) return;
    following = { school: seed };
    observing.cameraMoved();
    scene.follow(null);
    return;
  }
  // A whale, a honu or a seal first, whichever is nearest the tap for its size (M152): they are drawn over the fish.
  const animal = drawn.view.pickAnimal(scene.camera, x, y, r.width, r.height, 24);
  const seed = animal ? -1 : drawn.view.pickSchool(scene.camera, x, y, r.width, r.height, 24);
  if (!animal && seed < 0) return;
  following = animal ?? { school: seed };
  hud.habitatLeft();
  // An animal followed during a watched day: the camera the player's, not given back (M104).
  observing.cameraMoved();
  scene.follow(null);
});

function setSpeed(next: SpeedSetting): void {
  speed = next;
  if (next !== PAUSED) lastNonZeroSpeed = next;
  hud.setSpeed(next);
}

bindKeyboard({
  // Space during an advance stops it and stays paused (M240's second review: it had run the clock on at the last speed).
  togglePause: () => pressSpeed(speed === PAUSED && !advancing ? lastNonZeroSpeed : PAUSED),
  setSpeed: pressSpeed,
  escape: () => hud.escape(),
  menuOpen: () => hud.menuOpen,
});

// The simulation advances on its own accumulator (`clockSteps`, M288: apart from whatever draws), so a dropped frame
// costs a frame and not a day, and the clock runs at the same rate on any display, with the 3D view or without it.
const AUTOSAVE_INTERVAL_MS = 30_000;
let accumulator = 0;
let lastFrame = performance.now();
let lastAutosave = lastFrame;
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
/**
 * The world written to the autosave's slot, with the day it was written and the commands applied by then, so an
 * unchanged world is not written again and one changed by a command on its day is.
 */
let autosavedDay = -1;
let autosavedCommands = -1;
let autosavedWorld: World | null = null;
// The autosave the game could not open, said once the HUD is up (review 29).
/**
 * A game opened from a save waits paused (M289, the playtest's fourth usability point: a game saved paused ran on when
 * reloaded): the clock is the player's to start, the menu's own hold given back as a pause where it is open.
 */
function holdPaused(): void {
  if (hud.menuOpen) hud.setSpeedAfterMenu(PAUSED);
  else setSpeed(PAUSED);
}
if (typeof resumed !== 'string') {
  holdPaused();
  hud.say('Your game, where you left it, paused. Press Play to run on.');
} else {
  // A new game waits paused too (M319, the casual year's H1: four days ran while its first page was read). GAME CHOICE:
  // the days move when the player says, by Play or an objective's Advance, as a loaded game's do since M289.
  holdPaused();
}
if (unreadable) hud.say(`The saved game could not be opened (${unreadable.replace(/\.$/, '')}). It is kept aside, and a new game has begun.`);
function autosave(): void {
  // Never the world the game failed in (M288's review: the failure screen's Reload, leaving the page, had written it
  // over the last good autosave the screen promises is kept).
  if (hasFailed()) return;
  if (world.day === autosavedDay && world === autosavedWorld && commandsApplied === autosavedCommands) return;
  autosavedDay = world.day;
  autosavedWorld = world;
  autosavedCommands = commandsApplied;
  writeSlot(AUTOSAVE_SLOT, world);
}
// The page left, hidden or closed (a phone's app switched, a tab shut): saved then too (M237), so nothing since the last
// thirty seconds is lost.
document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') autosave(); });
window.addEventListener('pagehide', () => autosave());

/**
 * An advance to the next event under way (M240, the brief's section 9): the epoch day it runs to, every day ticked as
 * the clock ticks it, `ADVANCE_TICKS_PER_FRAME` a frame so the page stays live, stopped early on a warning the days bring.
 */
let advancing: { target: number; what: string } | null = null;
/** The days an advance steps a frame (JUDGED: the top speed's own, `MAX_TICKS_PER_FRAME`). */
const ADVANCE_TICKS_PER_FRAME = MAX_TICKS_PER_FRAME;
function advance(): void {
  if (!advancing) return;
  for (let i = 0; i < ADVANCE_TICKS_PER_FRAME; i++) {
    const report = tick(world);
    const stop = report.events.find((e) => stopsAdvance(e.severity));
    // A result home stops it too, its card opened (M320, the casual year's H2: an advance had run past the first
    // cleanup's return, its tonnes never seen).
    const results = [...report.events.filter(isResult), ...seasonDecided()];
    if (results.length > 0) {
      // A warning the same day said too (M320's review: it had taken the result's place).
      if (stop) hud.say(stopWords(world, stop));
      advancing = null;
      hud.resultsCame(results);
      return;
    }
    if (epochDayOf(world) >= advancing.target || stop || world.gameOver) {
      // The way on said (M262: the objective's button waits where it was, pressed again to run on).
      hud.say(stop ? stopWords(world, stop) : `${plainTitle(advancing.what)}: ${dateLabel(epochDayOf(world))}.`);
      advancing = null;
      return;
    }
  }
}

/**
 * The page's frame (M288: the clock and the HUD, then the drawing, each apart). The clock steps the world as the speed
 * asks (`clockSteps`), whatever draws or draws nothing; a failure of the simulation or the HUD ends the game on its
 * failure screen (`showFailure`), never hidden behind the 3D view's substitute; a failure of the 3D view's drawing frees
 * it and the game goes on without it (`stopGraphics`).
 */
function frame(now: number): void {
  // Before the work, so the interval it records is the whole frame: the simulation, the drawing and whatever the
  // browser did between (M166, as Sable Reserve's); not while the graphics are lost, where a quick frame drawing
  // nothing would read as smooth beside a still picture.
  if (!graphicsLost) frameMeter.record(now);
  // Never negative: the browser stamps a frame with its start, which can come before the page's clock began (M234).
  const elapsed = Math.max(0, now - lastFrame);
  // The aquarium's hour on the player's own seconds, a slow frame counted whole (M96's review: capped at 250 ms, a
  // watched day under four frames a second ran long), a hidden tab's return at most a second.
  const realS = Math.min(elapsed, 1000) / 1000;
  lastFrame = now;
  try {
    // No day is stepped while a day is watched (M96): its speed is held at pause, and this holds it whatever else.
    // An advance held while the menu holds the clock (M240's review: its days had run on behind the menu).
    if (advancing && !observing.watching) {
      if (!hud.menuOpen) advance();
    } else if (!world.gameOver && !observing.watching) {
      const step = clockSteps(accumulator, elapsed, speed);
      accumulator = step.accumulator;
      // A result home stops the clock and opens its card (M320); the frame's other days are not run (the clock held).
      for (let i = 0; i < step.steps; i++) {
        const results = [...tick(world).events.filter(isResult), ...seasonDecided()];
        if (results.length > 0) { hud.resultsCame(results); break; }
      }
    }
  } catch (error) {
    showFailure('The simulation stopped', error);
    return;
  }
  let hour: number;
  try {
    // A month closed (M289, the playtest's fifth point): its summary over the game, once the days stop running for an
    // advance (the last month's alone, an advance over many told once). Within the guard (review 33: a failure here had
    // frozen the game with no failure screen).
    if (!advancing && monthKey(world) !== monthShown) {
      monthShown = monthKey(world);
      hud.monthClosed();
    }
    // Nothing written while a day is watched, the world held (the brief's section 6.4).
    if (now - lastAutosave > AUTOSAVE_INTERVAL_MS && !observing.watching) {
      lastAutosave = now;
      autosave();
    }
    hud.update(world);
    // The next event's button (M240): what it is and how far, or the advance under way.
    hud.setNextEvent(advancing ? { label: `Advancing… ${Math.max(0, advancing.target - epochDayOf(world))} d`, busy: true } : nextEventLabel(world));
    const sanctuary = shown();
    // The hour the aquarium is drawn at: the observation clock's (M95), its preset kept on the day's own sunrise and
    // sunset as the days go, run on toward a newly chosen one by the player's seconds.
    if (sanctuary) observing.follow(sanctuary.env.dayLengthHours);
    hour = observing.advance(realS);
    const moon = moonLit(moonAgeDays(epochDayOf(world)), SYNODIC_MONTH_DAYS);
    hud.setObservation(observing.watching ? 'Watching one day' : PRESET_NAMES[observing.preset], clockLabel(hour), moon, observationLine(epochDayOf(world), hour, sanctuary?.name ?? ''));
    hud.setWatching(observing.watching ? { progress: observing.progress() ?? 0, playing: observing.watching.playing, locked: observing.watching.locked } : null);
    // Each species' adults as the days go, for the wildlife card's trend.
    if (sanctuary?.fish && world.day !== seenDay) {
      seenDay = world.day;
      for (const life of FISH_LIVES) {
        const adults = fishCounts(life, sanctuary.fish[life.id], epochDayOf(world)).adults;
        const was = seen.get(life.id);
        seen.set(life.id, was && world.day >= was.firstDay ? { ...was, lastDay: world.day, lastAdults: adults } : { firstDay: world.day, firstAdults: adults, lastDay: world.day, lastAdults: adults });
      }
    }
    if (!gl) substituteFrame(now, hour);
  } catch (error) {
    showFailure('The game stopped', error);
    return;
  }
  if (gl) {
    try {
      drawFrame(now, Math.min(elapsed, MAX_FRAME_MS) / 1000, hour);
    } catch (error) {
      stopGraphics(error);
    }
  }
  requestAnimationFrame(frame);
}

/**
 * The 3D view's frame: the whole tank's day laid and lit at the hour, its fish swum on the player's own seconds, the
 * animal followed kept in view with its card, or a habitat's own scene (`habitatFrame`); and drawn.
 */
function drawFrame(now: number, seconds: number, hour: number): void {
  // The whole tank's day laid only while it is shown; a habitat's scene takes its own (`opened.update`).
  if (!opened) lightAquarium();
  const sanctuary = shown();
  // The exhibit lit by the sun at that hour on the day (the map keeps its noon sun, the scientific view's).
  if (sanctuary && drawn.style === 'exhibit' && hour !== litHour && !opened) {
    litHour = hour;
    drawn.view.setLight(hourSun(sanctuary, hour));
  }
  // The fish bound where the hour has them, swimming there at their own pace (M88), on the animation clock: the
  // player's own seconds, whatever the game's speed and while it is paused (M95, the brief: "animations should
  // remain smooth when the ecological simulation is paused"; M88's paused stillness was the user's word then).
  // Still within themselves for a player who asks for less motion, who sees them swim out and home all the same.
  if (sanctuary && !opened) view.swimFish(hour, sanctuary.env.dayLengthHours, seconds, reducedMotion.matches);
  // A habitat's own scene (M110) drawn in place of the whole tank, which waits.
  if (opened) {
    habitatFrame(opened, sanctuary, hour, now, seconds);
    return;
  }
  // The school followed drawn whole, whatever the level of detail (M105).
  if (drawn.style === 'exhibit') drawn.view.holdSchool(following !== null && 'school' in following ? following.school : null);
  // The map's school followed (M152): the camera kept on it, its card told afresh; nothing else is drawn there.
  if (following !== null && drawn.style === 'map') {
    const school = 'school' in following ? drawn.view.schoolAt(following.school) : null;
    if (!school) unfollow();
    else {
      scene.follow(school.middle.toArray());
      hud.setWildlife(tagging(wildlifeCard(school, seen.get(school.species) ?? null), { species: school.species, island: school.island, stage: 'adult' }));
    }
  }
  // The school or the whale followed: the camera kept on it, its card told afresh.
  if (following !== null && drawn.style === 'exhibit') {
    if ('school' in following) {
      const school = drawn.view.schoolAt(following.school);
      if (!school) unfollow();
      else {
        scene.follow(school.middle.toArray());
        hud.setWildlife(tagging(wildlifeCard(school, seen.get(school.species) ?? null), { species: school.species, island: school.island, stage: 'adult' }));
      }
    } else if ('honu' in following) {
      const honu = drawn.view.turtleAt(following.honu.adult, following.honu.index);
      const turtles = sanctuary?.turtles;
      if (!honu || !turtles || !sanctuary) unfollow();
      else {
        const c = turtleCounts(HONU, turtles.honu, epochDayOf(world));
        // Framed with half its length again either side (JUDGED, the whale's).
        scene.follow(honu.middle.toArray(), 1.5 * honu.width);
        hud.setWildlife(tagging(honuCard({ ...honu, restM: honu.places.restM, perSprite: HONU_DRAW.turtlesPerSprite, young: c.young, adults: c.adults, away: c.awayFemales + c.awayMales }), { species: 'honu', stage: following.honu.adult ? 'adult' : 'young' }));
      }
    } else if ('seal' in following) {
      const seal = drawn.view.sealAt(following.seal);
      const seals = sanctuary?.seals;
      if (!seal || !seals) unfollow();
      else {
        const stages = sealStages(MONK_SEAL, seals.monk);
        // Framed with half its length again either side (JUDGED, the honu's).
        scene.follow(seal.middle.toArray(), 1.5 * seal.width);
        hud.setWildlife(tagging(sealCard({ ...seal, lengthM: SEAL_BODY.lengthM[seal.sprite.stage], perSprite: SEAL_DRAW.sealsPerSprite, atSea: stages, nursing: stages.nursing, population: sealCounts(MONK_SEAL, seals.monk, epochDayOf(world)).population }), { species: 'monkSeal', stage: seal.sprite.stage === 'adult' ? 'adult' : 'young' }));
      }
    } else if ('whale' in following) {
      const whale = drawn.view.whaleAt(following.whale);
      const whales = sanctuary?.whales;
      if (!whale || !whales) unfollow();
      else {
        const epochDay = epochDayOf(world);
        const c = whaleCounts(HUMPBACK, whales.humpback, epochDay);
        // Framed with half its length again either side (JUDGED).
        scene.follow(whale.middle.toArray(), 1.5 * WHALE_DRAW.lengthUnits);
        hud.setWildlife(tagging(whaleCard({ ...whale, perSprite: WHALE_DRAW.whalesPerSprite, inHawaii: c.inHawaii, calvesInHawaii: humpbackSprites(c, epochDay).calvesInHawaii, population: c.population }), { species: 'humpback', stage: 'adult' }));
      }
    } else unfollow();
  }
  // The ripple drifts on the player's clock (M77), still for a player who asks for less motion.
  if (!reducedMotion.matches) view.drift(seconds);
  scene.setInsets(hud.insets());
  scene.update(now);
  // The depth ruler kept in the part of the screen the panels leave (M104), once a move between habitats has ended
  // (M104's review: it fought `rulerAt` through the move and ended elsewhere).
  if (drawn.style === 'exhibit' && !scene.moving()) {
    const inset = hud.insets();
    const width = Math.max(1, canvas.clientWidth);
    drawn.view.keepRulerInView(scene.camera, -1 + (2 * inset.left) / width, 1 - (2 * inset.right) / width);
  }
  // The fish's level of detail measured over the part of the canvas the panels leave, as the camera centers on it (M105).
  const [cw, ch] = [Math.max(1, canvas.clientWidth), Math.max(1, canvas.clientHeight)];
  const open = visibleRect(cw, ch, hud.insets());
  view.fitLabels(scene.camera, ch, seconds, [-1 + (2 * open.left) / cw, 1 - (2 * open.right) / cw, -1 + (2 * open.bottom) / ch, 1 - (2 * open.top) / ch]);
  showMarks(view, scene.camera, ch);
  scene.controls.update();
  scene.renderer.render(scene.scene, scene.camera);
}


/** The sun and the moon at an hour of the world's day at a place (M95, M104): the observation clock's light. */
function hourSun(sanctuary: Place, hour: number): HourSun {
  const epochDay = epochDayOf(world);
  const noon = daySun(sanctuary.site.latDeg, epochDay, sanctuary.env);
  const declination = solarDeclinationDeg(noonUtc(epochDay));
  // The moon by its phase and its height on the ecliptic (M104), lighting the night where it is up.
  // Its age at the hour drawn, the local solar hour's instant (M104's second review: the date's 00:00 UTC for the
  // whole day put it up to 17.5° behind at Maui Nui).
  const moon = moonAtHour(sanctuary.site.latDeg, sunLongitudeDeg(epochDay), moonAgeDays(epochDay + (hour - sanctuary.site.lonDeg / 15) / 24), SYNODIC_MONTH_DAYS, hour);
  return { ...sunAtHour(sanctuary.site.latDeg, declination, noon, hour), moon };
}

/**
 * A frame of a habitat's own scene (M110): the day's state laid on it when the day turns (the adapter's, read from the
 * world; at most once a second at speed, `relayDue`, M182), lit at the observation clock's hour (and again as the
 * camera's depth changes, the water's light being its depth's), its animals swum on the player's own seconds, the
 * animal followed kept in view with its card, the camera moved and held in its bounds, its level of detail fitted, and
 * drawn.
 */
function habitatFrame(o: OpenedHabitat, sanctuary: Place | undefined, hour: number, now: number, seconds: number): void {
  if (!sanctuary) return;
  if (relayDue(openedFor, world, now)) {
    openedFor = { world, day: world.day, at: now };
    o.update(world, sanctuary);
    openedLit.hour = Number.NaN;
    hud.setDrawingNote(o.note(), '', false);
  }
  const s = o.scene;
  const depthM = -s.camera.position.y;
  if (hour !== openedLit.hour || Math.abs(depthM - openedLit.depthM) > 0.5) {
    openedLit = { hour, depthM };
    s.setLight(hourSun(sanctuary, hour));
  }
  s.animate(hour, sanctuary.env.dayLengthHours, seconds, reducedMotion.matches);
  // The way on asked for, taken once its habitat is open (M147): the visitor come in its place, or its kind's school.
  if (onwardAsked && s.kind === onwardAsked.habitat && !veil.covering()) {
    const then = onwardAsked.then;
    onwardAsked = null;
    // A humpback gone back to the Blue Water followed on as the first whale drawn there (M210).
    const whale = 'species' in then && then.species === 'humpback' && s instanceof PelagicScene ? s.whalesDrawn().find((w) => !w.calf)?.index ?? null : null;
    const seed = 'species' in then && then.species !== 'humpback' && s instanceof ReefScene ? s.nearestSchool(then.species) : null;
    following = 'visitor' in then ? { visitor: then.visitor } : whale !== null ? { whale } : seed !== null ? { school: seed } : null;
  }
  // The one fish tapped followed (M266, Follow Animal), in whichever school of its kind it swims now; taken by a hunt
  // or gone as a day's counts fell (the last of its kind to go, never kept for being followed), its school followed on.
  let fish: ReturnType<ReefScene['fishAt']> = null;
  if (following !== null && 'school' in following && following.fish !== undefined && s instanceof ReefScene) {
    const one = s.fishAt(following.fish);
    if (one && !one.eaten) [following, fish] = [{ school: one.seed, fish: following.fish }, one];
    else {
      // Its school followed on where it stays; where its school leaves too, the follow ends (review 31: the toast named a
      // school as followed that was leaving with it).
      const school = s.schoolAt(following.school) ? following.school : null;
      const what = one ? 'The fish you followed was caught by a predator' : 'The fish you followed has left the reef\'s drawing';
      hud.say(school !== null ? `${what}; following its school.` : `${what}, its school with it.`);
      if (school !== null) following = { school };
      else unfollow();
    }
  }
  if (s instanceof ReefScene) {
    s.holdSchool(following !== null && 'school' in following ? following.school : null);
    s.holdFish(following !== null && 'school' in following ? following.fish ?? null : null);
  }
  onward = null;
  if (following !== null) {
    if ('school' in following && s instanceof ReefScene) {
      const school = s.schoolAt(following.school);
      if (!school) unfollow();
      else {
        habitatHost.follow((fish?.at ?? school.middle).toArray());
        const perHa = school.young ? s.youngPerHa(school.species) : s.fishPerHa(school.species, school.island);
        const species = school.species;
        const whole = wildlifeCard({ ...school, local: { place: s.locationName, perHa, drawnTimes: REEF_SCENE.fish.drawnTimes, young: school.young } }, isCountedFish(species) ? null : seen.get(species) ?? null);
        const card = fish ? oneFishCard(whole, { lengthMm: fish.lengthMm, terminal: fish.terminal, young: school.young, perSprite: school.perSprite, counted: isCountedFish(species) }) : whole;
        // A fish counted, not simulated (M231), has no stock to tag a fish of nor a way it crosses to another habitat.
        if (isCountedFish(species)) hud.setWildlife(card);
        else hud.setWildlife(tagging(school.young ? card : withCrossing(card, species, sanctuary, hour), school.young ? { species, island: school.island, stage: 'young', minLengthMm: YOUNG_SEEN_MM } : { species, island: school.island, stage: 'adult' }));
      }
    } else if ('whale' in following && s instanceof PelagicScene && sanctuary.whales) {
      const whale = s.whaleAt(following.whale);
      if (!whale) unfollow();
      else {
        const epochDay = epochDayOf(world);
        const c = whaleCounts(HUMPBACK, sanctuary.whales.humpback, epochDay);
        // Framed with half its length again either side (JUDGED, the whole tank's).
        habitatHost.follow(whale.middle.toArray(), 1.5 * PELAGIC_SCENE.whaleM);
        // With the way on to the Reef where one crossed there today (M210).
        hud.setWildlife(tagging(withCrossing(whaleCard({ ...whale, perSprite: WHALE_DRAW.whalesPerSprite, inHawaii: c.inHawaii, calvesInHawaii: humpbackSprites(c, epochDay).calvesInHawaii, population: c.population }), 'humpback', sanctuary, hour), { species: 'humpback', stage: 'adult' }));
      }
    } else if ('dolphin' in following && s instanceof ReefScene) {
      // A spinner dolphin resting over the Reef (M216); let go once its group has gone out.
      const d = s.dolphinAt(following.dolphin);
      if (!d) unfollow();
      else {
        // Framed with half its length again either side (JUDGED, the whole tank's).
        habitatHost.follow(d.middle.toArray(), 1.5 * d.width);
        hud.setWildlife(dolphinCard({ ...d, lengthM: d.width, depthM: -d.middle.y }));
      }
    } else if ('bottomfish' in following && s instanceof BenthicScene) {
      // A Deep 7 fish on the Slope's floor (M215); let go once it has faded out with the day's count.
      const f = s.slopeFishAt(following.bottomfish);
      if (!f) unfollow();
      else {
        // Framed with half its length again either side (JUDGED, the whole tank's).
        habitatHost.follow(f.middle.toArray(), 1.5 * f.width);
        hud.setWildlife(slopeFishCard({ ...f, depthM: -f.middle.y, drawnTimes: VISITOR_DRAW.fishTimes }));
      }
    } else if ('octopus' in following && s instanceof ReefScene) {
      // A day octopus out of its den (M158); let go once it has faded from where it stood as its forage ends.
      const o = s.octopusAt(following.octopus);
      if (!o) unfollow();
      else {
        // Framed with half its width again either side (JUDGED, the whole tank's).
        habitatHost.follow(o.middle.toArray(), 1.5 * o.width);
        hud.setWildlife(tagging(octopusCard({ ...o.octopus, island: ISLAND_NAMES[REEF_ISLANDS[o.octopus.island] as (typeof REEF_ISLANDS)[number]], color: o.color, fadingTo: o.fadingTo, depthM: -(o.middle.y - o.height / 2), inHectare: o.inHectare, outNow: o.outNow, perHa: o.perHa, mantleM: mantleM(o.octopus.weightKg), drawnTimes: REEF_SCENE.fish.drawnTimes, backAt: o.backAt }), { species: 'octopus', island: o.octopus.island, stage: o.octopus.young ? 'young' : 'adult' }));
      }
    } else if ('visitor' in following) {
      // The day's honu or seal in any habitat (M152); gone once it has faded out. A species' visitor (M147) gone home at
      // its slot's swap kept on its card, with the way home.
      const v = s.visitorAt(following.visitor);
      // Handed over to a tagged one as drawn (M203's second review: the follow ended), followed on under its name.
      if (v && v.id !== following.visitor) following = { visitor: v.id };
      const gone = v ? null : s.visitorGone(following.visitor);
      const visitor = v?.visitor ?? gone;
      if (visitor?.slot && (v?.visit || gone)) {
        const slot = visitor.slot;
        const left = gone !== null || (!slot.after && hour >= slot.hour);
        if (v) habitatHost.follow(v.middle.toArray(), 1.5 * v.width);
        const use = SPECIES_VISITS[visitor.kind as VisitorSpecies].visits.find((u) => u.habitat === s.kind);
        const note = v?.visit ?? s.visitOf(visitor.kind);
        hud.setWildlife(speciesVisitorCard({
          species: visitor.kind as VisitorSpecies, stage: visitor.stage === 'terminal' ? 'terminal' : 'adult', lengthM: visitor.lengthM,
          depthM: v && !left ? -v.middle.y : null, habitat: HABITAT_NAMES[s.kind] ?? s.locationName, home: HABITAT_NAMES[slot.home] ?? slot.home,
          count: note?.count ?? 0, expected: note?.expected ?? 0, perAreaOfHome: use?.value ?? 0, source: (use?.source ?? '').match(/^[^:]*?\d{4}/)?.[0] ?? '',
          swapHour: slot.hour, after: slot.after, gone: left, drawnTimes: VISITOR_DRAW.fishTimes,
        }));
        if (left) onward = { habitat: slot.home, then: { species: visitor.kind as VisitorSpecies } };
      } else if (!v) {
        // A tagged one followed whose tag has it in another habitat today (M203's review: let go, no way on): its card
        // says where, with the way on to it there.
        const moved = movedTag(following.visitor, s.kind);
        if (moved) {
          if (moved.card) hud.setWildlife(moved.card);
          onward = moved.onward;
        } else unfollow();
      } else {
        // Framed with half its length again either side (JUDGED, the whole tank's).
        habitatHost.follow(v.middle.toArray(), 1.5 * v.width);
        const habitat = drawn.style === 'exhibit' ? drawn.exhibit.habitats.find((h) => h.id === o.habitat)?.name ?? s.locationName : s.locationName;
        const hv = v.visitor;
        // A manta's card (M242), no tag of it either: not simulated as a population (M223).
        if (hv.kind === 'manta' && hv.stage !== 'terminal') hud.setWildlife(visitorCard({ ...hv, kind: 'manta', stage: hv.stage, depthM: -v.middle.y, habitat, howOften: daysIn(v.odds) }));
        // A whitetip's card (M235), no tag of it: the game tags none (its stock is not among the tagged's).
        else if (hv.kind === 'whitetip' && hv.stage !== 'terminal') hud.setWildlife(visitorCard({ ...hv, kind: 'whitetip', stage: hv.stage, depthM: -v.middle.y, habitat, howOften: daysIn(v.odds), active: whitetipActive(hour, sanctuary.env.dayLengthHours) }));
        else if ((hv.kind === 'honu' || hv.kind === 'honuea' || hv.kind === 'seal' || hv.kind === 'tigerShark') && hv.stage !== 'terminal') {
          // Tagged here and named, or offered a tag (M203): its kind's tag, a young one's for a pup or a juvenile.
          const species = hv.kind === 'seal' ? 'monkSeal' : hv.kind;
          hud.setWildlife(tagging(visitorCard({ ...hv, kind: hv.kind, stage: hv.stage, depthM: -v.middle.y, habitat, howOften: daysIn(v.odds), tagged: hv.tag?.name ?? null, offShelf: s.kind === 'blue' }), { species, stage: hv.stage === 'adult' ? 'adult' : 'young', ...(hv.cls !== undefined ? { cls: hv.cls } : {}) }, hv.tag !== undefined));
        }
      }
    } else unfollow();
  }
  habitatHost.setInsets(hud.insets());
  habitatHost.update(now);
  const [cw, ch] = [Math.max(1, canvas.clientWidth), Math.max(1, canvas.clientHeight)];
  const open = visibleRect(cw, ch, hud.insets());
  s.fit(ch, seconds, [-1 + (2 * open.left) / cw, 1 - (2 * open.right) / cw, -1 + (2 * open.bottom) / ch, 1 - (2 * open.top) / ch]);
  showMarks(s, s.camera, ch);
  // Empty off the Reef (review M267's: the last strike's text stayed frozen there).
  if (huntOverlay?.shown()) huntOverlay.show(s instanceof ReefScene ? s.huntsDebug() : []);
  if (lifeOverlay?.shown()) {
    const life = s instanceof ReefScene ? s.lifeDebug() : { acts: [], octopuses: [] };
    lifeOverlay.show(life.acts, life.octopuses);
  }
  scene.renderer.render(s.scene, s.camera);
}

// The 3D view, unless the address turns it off (`?render=off`, M288: for a browser without WebGL and for playtesting the
// game's management); where it cannot start, its substitute. The game's own is up either way.
if (renderModeOf(location.search) === 'off') startSubstitute(OFF_3D_NOTICE);
else startGraphics();
hud.update(world);
markStarted();
requestAnimationFrame(frame);
